import { ArrowLeft, Check, Code, Loader2, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LanguageProvider, type AddLanguageRequest } from './context/LanguageContext';
import { LANGUAGES_LIST } from '@/data/languages';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { fields, widgets } from '@/data/rjsf';
import { trpc } from '@/lib/api/trpc';
import { nativeCheckoutSchemaComplete, nativeCheckoutUiSchemaComplete } from './FormPreview';
import { PresetManager } from './PresetManager';
import { GroupedFormEditor } from './components/GroupedFormEditor';
import { PreviewWithPageTabs, type PreviewWithPageTabsRef } from './components/PreviewWithPageTabs';
import { deepEqual } from './utils/deep-equal';
import { useConfigHotReload } from './utils/hotReload';
import { filterResourceFieldsFromSchema } from './utils/resourceFieldFilter';

interface ConfigEditorProps {
  instanceId: string;
  initialConfig?: any;
  previewUrl: string;
  isPreview?: boolean;
  onClose: () => void;
  deploymentId?: string;
  storeId?: string;
  initialPagePath?: string;
}

export function ConfigEditor({
  instanceId,
  previewUrl,
  isPreview = false,
  onClose,
  deploymentId,
  initialPagePath,
}: ConfigEditorProps) {
  const { toast } = useToast();
  const { t } = useTranslation('storefront');
  const utils = trpc.useUtils();

  // Preset management mutations
  const createPresetMutation = trpc.pluginPresets.create.useMutation();
  const updatePresetMutation = trpc.pluginPresets.update.useMutation();

  // Fetch instance data with deployment (includes schema and UI schema)
  // Refetch on mount to ensure fresh data (config may have changed)
  const { data: instance, isLoading } = trpc.plugins.getInstanceById.useQuery(
    { instanceId },
    {
      enabled: !!instanceId,
      refetchOnMount: 'always', // Always refetch when editor opens
      refetchOnWindowFocus: false, // Don't refetch on window focus to avoid interruptions
    },
  );

  // Fetch preview pages with full URLs and requirements
  const { data: previewPages } = trpc.plugins.getInstancePreviewPages.useQuery(
    { instanceId },
    {
      enabled: !!instanceId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const initialConfig = instance?.config || {};
  const storeId = instance?.storeId;

  // Get schema and UI schema from deployment (fallback to hardcoded native checkout schema)
  // Filter out resource fields - they're handled in Step Editor Panel → Design tab → Resources section
  const rawConfigSchema = instance?.deployment?.configSchema || nativeCheckoutSchemaComplete;
  const configSchema = filterResourceFieldsFromSchema(rawConfigSchema);
  const configUiSchema = instance?.deployment?.configUiSchema || nativeCheckoutUiSchemaComplete;

  // Log schema source and config for debugging
  useEffect(() => {
    if (instance) {
      const hasDeploymentSchema = !!instance?.deployment?.configSchema;
      const hasDeploymentUiSchema = !!instance?.deployment?.configUiSchema;
      console.log('[ConfigEditor] 📝 Instance loaded:', {
        instanceId,
        deploymentId: instance.deploymentId,
        hasDeploymentSchema,
        hasDeploymentUiSchema,
        usingFallback: !hasDeploymentSchema || !hasDeploymentUiSchema,
        schemaProperties: hasDeploymentSchema
          ? Object.keys(instance.deployment.configSchema?.properties || {})
          : [],
      });

      console.log('[ConfigEditor] 🎨 Initial config loaded from instance:', {
        instanceId,
        configKeys: Object.keys(initialConfig),
        configSample: Object.keys(initialConfig).slice(0, 10),
        hasConfig: !!instance.config,
        fullConfig: initialConfig,
      });
    }
  }, [instance, instanceId]);

  const [configData, setConfigData] = useState<Record<string, unknown>>(initialConfig);
  const [jsonConfig, setJsonConfig] = useState(JSON.stringify(initialConfig, null, 2));
  const [editMode, setEditMode] = useState<'ui' | 'json'>('ui'); // Start with UI mode
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [iframeKey, setIframeKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [editableConfigName, setEditableConfigName] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null); // Track selected preset
  const [showForkDialog, setShowForkDialog] = useState(false);
  const [forkPresetName, setForkPresetName] = useState('');
  const [currentPagePath, setCurrentPagePath] = useState<string | undefined>(undefined); // Track current page path for x-pages filtering
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUserEditedRef = useRef(false); // Track if user has actually edited
  const lastSavedConfigRef = useRef<Record<string, unknown>>({});
  const isModeSwitchingRef = useRef(false); // Track if we're switching modes
  const previewRef = useRef<PreviewWithPageTabsRef>(null);
  const pendingLanguageChangeRef = useRef<string[] | null>(null); // Track pending language change when forking

  // Hot reload config changes to preview iframe (no page reload needed)
  useConfigHotReload(configData, true);

  // Get available presets from deployment
  const configPresets = instance?.deployment?.configPresets || {};
  const presetNames = Object.keys(configPresets);
  const hasPresets = presetNames.length > 0;

  // Initialize state when instance data loads
  useEffect(() => {
    if (instance?.config) {
      const config = instance.config;
      setConfigData(config);
      setJsonConfig(JSON.stringify(config, null, 2));
      // Use meta.name (new format) or fallback to configName (old format)
      setEditableConfigName((config as any).meta?.name || (config as any).configName || 'Untitled');
      // Restore selected preset from config metadata
      const presetId = (config as any).meta?.presetId || null;
      console.log('[ConfigEditor] 🎯 Initializing with presetId:', {
        presetId,
        presetName: (config as any).meta?.presetName,
        presetType: (config as any).meta?.presetType,
        isOfficialPreset: presetId?.startsWith('deployment:'),
      });
      setSelectedPresetId(presetId);
      lastSavedConfigRef.current = JSON.parse(JSON.stringify(config));
    }
  }, [instance]);

  // Handle preset selection
  const handlePresetChange = (presetName: string) => {
    const presetConfig = configPresets[presetName];
    if (presetConfig) {
      hasUserEditedRef.current = true; // Mark as edited
      setConfigData(presetConfig);
      setJsonConfig(JSON.stringify(presetConfig, null, 2));
      toast({
        title: t('configEditor.editor.presetApplied'),
        description: t('configEditor.editor.presetAppliedDesc', { name: presetName }),
      });
    }
  };

  // Handle config name change
  const handleConfigNameChange = (newName: string) => {
    setEditableConfigName(newName);
    hasUserEditedRef.current = true; // Mark as edited

    // Update configData with new name in meta field (new format)
    setConfigData((prev) => ({
      ...prev,
      meta: {
        ...(prev as any).meta,
        name: newName,
      },
      // Keep configName for backward compatibility (will be removed later)
      configName: newName,
    }));
  };

  // Sync between configData and jsonConfig when switching modes
  useEffect(() => {
    isModeSwitchingRef.current = true; // Mark that we're switching modes

    if (editMode === 'json') {
      setJsonConfig(JSON.stringify(configData, null, 2));
    } else {
      try {
        const parsed = JSON.parse(jsonConfig);
        setConfigData(parsed);
      } catch {
        // Invalid JSON, keep current configData
      }
    }

    // Reset the flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isModeSwitchingRef.current = false;
    }, 100);
  }, [editMode]);

  // Sync jsonConfig when configData changes (for JSON mode consistency)
  useEffect(() => {
    if (editMode === 'json' && !isModeSwitchingRef.current) {
      setJsonConfig(JSON.stringify(configData, null, 2));
    }
  }, [configData, editMode]);

  // Update instance config mutation (edits the forked instance directly)
  const updateConfigMutation = trpc.plugins.updateInstanceConfig.useMutation({
    onSuccess: (_, variables) => {
      setIsSaving(false);
      setLastSaved(new Date());
      // Update last saved config reference with the actual saved config
      lastSavedConfigRef.current = JSON.parse(JSON.stringify(variables.config));
      // Auto-refresh preview after save (DISABLED: hot reload handles updates automatically)
      // setIframeKey((prev) => prev + 1);
    },
    onError: (error) => {
      setIsSaving(false);
      toast({
        title: t('configEditor.editor.autoSaveFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Debounced auto-save on config change (only if data actually changed)
  useEffect(() => {
    // Skip auto-save if:
    // 1. User hasn't edited yet (prevents save on initial mount)
    // 2. We're currently switching modes (prevents save on mode switch)
    // 3. Data hasn't actually changed (deep comparison)
    if (!hasUserEditedRef.current || isModeSwitchingRef.current) {
      console.log('[Auto-Save] Skipped:', {
        hasEdited: hasUserEditedRef.current,
        isSwitching: isModeSwitchingRef.current,
      });
      return;
    }

    // Deep comparison: only save if data has actually changed
    if (deepEqual(configData, lastSavedConfigRef.current)) {
      console.log('[Auto-Save] Skipped: No changes detected');
      return;
    }

    console.log('[Auto-Save] Scheduling save...', { configName: configData.configName });

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    saveTimeoutRef.current = setTimeout(async () => {
      // Double-check: ensure data still changed before saving
      let currentConfig = configData;
      if (deepEqual(currentConfig, lastSavedConfigRef.current)) {
        console.log('[Auto-Save] Cancelled: No changes after delay');
        return; // Data hasn't changed, skip save
      }

      console.log('[Auto-Save] Saving config...', { configName: currentConfig.configName });
      try {
        setIsSaving(true);

        // Handle preset update/fork
        currentConfig = await handlePresetUpdate(currentConfig);

        await updateConfigMutation.mutateAsync({
          instanceId,
          config: currentConfig,
        });
        console.log('[Auto-Save] Saved successfully');
      } catch (error) {
        console.error('[Auto-Save] Error:', error);
        // Silent fail
        setIsSaving(false);
      }
    }, 2000);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [configData, instanceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleForkPreset = async (customName: string) => {
    if (!customName.trim()) {
      toast({
        title: t('configEditor.editor.nameRequired'),
        description: t('configEditor.editor.enterNameForPreset'),
        variant: 'destructive',
      });
      return;
    }

    const presetName = (configData as any)?.meta?.presetName;

    // Apply any pending language changes to the config before forking
    let configToFork = configData;
    if (pendingLanguageChangeRef.current) {
      configToFork = {
        ...configData,
        languages: pendingLanguageChangeRef.current,
      };
    }

    try {
      // Create forked preset via TRPC
      const result = await createPresetMutation.mutateAsync({
        pluginDeploymentId: instance?.deployment?.id,
        name: customName.trim(),
        description: `Forked from official preset: ${presetName}`,
        config: configToFork,
        isAiGenerated: false,
      });

      const newPresetId = `custom:${result.id}`;
      setSelectedPresetId(newPresetId);

      // Invalidate preset queries to refresh the list
      await utils.pluginPresets.list.invalidate({ pluginDeploymentId: instance?.deployment?.id });

      // Update config metadata to point to forked preset
      const updatedConfig = {
        ...configToFork,
        meta: {
          ...(configToFork as any).meta,
          presetId: newPresetId,
          presetName: customName.trim(),
          presetType: 'custom',
        },
      };

      // Update the local state immediately so the UI reflects the new preset
      setConfigData(updatedConfig);
      if (editMode === 'json') {
        setJsonConfig(JSON.stringify(updatedConfig, null, 2));
      }

      // Now allow editing
      hasUserEditedRef.current = true;

      // Clear pending language change
      pendingLanguageChangeRef.current = null;

      // Close dialog
      setShowForkDialog(false);
      setForkPresetName('');

      toast({
        title: t('configEditor.editor.presetCreated'),
        description: t('configEditor.editor.presetCreatedDesc', { name: customName }),
      });
    } catch (error: any) {
      console.error('[ConfigEditor] ❌ Failed to fork preset:', error);
      toast({
        title: t('configEditor.editor.forkFailed'),
        description: error.message || t('configEditor.editor.failedCreateCustom'),
        variant: 'destructive',
      });
      // Clear pending language change on error
      pendingLanguageChangeRef.current = null;
    }
  };

  const handleFormChange = async (data: any) => {
    // Check if editing an official preset - auto-fork it instantly
    if (!hasUserEditedRef.current && selectedPresetId?.startsWith('deployment:')) {
      // IMMEDIATELY mark as edited to prevent duplicate forks from rapid events (e.g., color picker drag)
      hasUserEditedRef.current = true;
      // Auto-fork with the new form data
      await autoForkAndApplyChanges(data.formData);
      return;
    }

    hasUserEditedRef.current = true;
    setConfigData(data.formData);
  };

  const handleJsonChange = async (newConfig: string) => {
    // Try to parse the JSON first
    let parsed: any;
    try {
      parsed = JSON.parse(newConfig);
    } catch {
      // Invalid JSON, just update the text (user is still typing)
      setJsonConfig(newConfig);
      return;
    }

    // Check if editing an official preset - auto-fork it instantly
    if (!hasUserEditedRef.current && selectedPresetId?.startsWith('deployment:')) {
      // IMMEDIATELY mark as edited to prevent duplicate forks
      hasUserEditedRef.current = true;
      // Auto-fork with the new parsed config
      await autoForkAndApplyChanges(parsed);
      return;
    }

    hasUserEditedRef.current = true;
    setJsonConfig(newConfig);
    // Only update if parsed JSON is different from current configData
    if (!deepEqual(parsed, configData)) {
      setConfigData(parsed);
    }
  };

  const handlePresetUpdate = async (config: any): Promise<any> => {
    // Handle preset updates before saving config
    if (!selectedPresetId) return config; // No preset selected, just save config

    const presetType = (config as any).meta?.presetType;
    const presetName = (config as any).meta?.presetName;

    // Only update if it's a custom preset (official presets handled by dialog)
    if (presetType === 'custom' && selectedPresetId.startsWith('custom:')) {
      // Custom preset - update it
      const presetId = selectedPresetId.replace('custom:', '');
      console.log('[ConfigEditor] 📝 Updating custom preset:', presetId);
      try {
        await updatePresetMutation.mutateAsync({
          presetId,
          config,
        });

        // Invalidate preset queries to refresh the list
        await utils.pluginPresets.list.invalidate({ pluginDeploymentId: instance?.deployment?.id });

        toast({
          title: t('configEditor.editor.presetUpdated'),
          description: t('configEditor.editor.presetUpdatedDesc', { name: presetName }),
        });
      } catch (error: any) {
        console.error('[ConfigEditor] ❌ Failed to update preset:', error);
        toast({
          title: t('configEditor.editor.updateFailed'),
          description: error.message || t('configEditor.editor.failedUpdatePreset'),
          variant: 'destructive',
        });
      }
    }

    return config;
  };

  const handleManualSave = async () => {
    // Clear the auto-save timeout and save immediately
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    try {
      let configToSave = configData;
      if (editMode === 'json') {
        configToSave = JSON.parse(jsonConfig);
        setConfigData(configToSave);
      }

      // Check if data has actually changed
      if (deepEqual(configToSave, lastSavedConfigRef.current)) {
        toast({
          title: t('configEditor.editor.noChanges'),
          description: t('configEditor.editor.noChangesDesc'),
          variant: 'default',
        });
        return;
      }

      setIsSaving(true);

      // Handle preset update/fork
      configToSave = await handlePresetUpdate(configToSave);

      await updateConfigMutation.mutateAsync({
        instanceId,
        config: configToSave,
      });
    } catch (error) {
      setIsSaving(false);
      const errorMessage = error instanceof Error ? error.message : 'Invalid JSON format';
      toast({
        title: t('configEditor.editor.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleCloseClick = () => {
    // Invalidate instance queries to ensure fresh data when returning
    utils.plugins.getAvailableInstancesForStepType.invalidate();
    utils.plugins.getInstanceById.invalidate();

    // Close editor (auto-save handles saving, all designs are permanent)
    onClose();
  };

  // Extract available languages by detecting actual translation objects in config
  const configLanguages = useMemo(() => {
    // Helper to detect if an object is a translation object (has 2-char language code keys)
    const isTranslationObject = (obj: any): boolean => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
      const keys = Object.keys(obj);
      // Must have at least one key, all keys are 2 chars (language codes), and values are strings
      return keys.length > 0 && keys.every((k) => k.length === 2 && typeof obj[k] === 'string');
    };

    // Recursively find all translation objects and collect their language keys
    const findLanguagesInConfig = (obj: any, languages: Set<string>): void => {
      if (!obj || typeof obj !== 'object') return;

      if (Array.isArray(obj)) {
        obj.forEach((item) => findLanguagesInConfig(item, languages));
        return;
      }

      // Check if this object is a translation object
      if (isTranslationObject(obj)) {
        Object.keys(obj).forEach((key) => languages.add(key));
        return; // Don't recurse into translation objects
      }

      // Recurse into nested objects
      Object.values(obj).forEach((value) => findLanguagesInConfig(value, languages));
    };

    // Scan config for translation objects
    const detectedLanguages = new Set<string>();
    findLanguagesInConfig(configData, detectedLanguages);

    // If we found languages in translation objects, use those
    if (detectedLanguages.size > 0) {
      // Sort with 'en' always first (primary language), rest alphabetical
      const langs = Array.from(detectedLanguages).sort((a, b) => {
        if (a === 'en') return -1;
        if (b === 'en') return 1;
        return a.localeCompare(b);
      });
      console.log('[ConfigEditor] 🌍 Detected languages from translations:', langs);
      return langs;
    }

    // Fallback to config.languages field
    if (configData?.languages && Array.isArray(configData.languages)) {
      return configData.languages;
    }

    // Fallback to schema default
    if (configSchema?.properties?.languages) {
      const languagesProperty = configSchema.properties.languages as any;
      if (languagesProperty.default && Array.isArray(languagesProperty.default)) {
        return languagesProperty.default;
      }
    }

    // Default fallback
    return ['en'];
  }, [configSchema, configData]);

  // Auto-fork official preset and apply changes
  const autoForkAndApplyChanges = async (changes: Partial<Record<string, unknown>>) => {
    const presetName = (configData as any)?.meta?.presetName || 'Custom';
    const customName = `${presetName} (Custom)`;

    console.log('[ConfigEditor] 🔀 Auto-forking official preset:', {
      presetName,
      customName,
      pluginDeploymentId: instance?.deployment?.id,
      deploymentId: instance?.deploymentId,
    });

    // Apply changes to config before forking
    const configToFork = {
      ...configData,
      ...changes,
    };

    try {
      // Create forked preset via TRPC
      const result = await createPresetMutation.mutateAsync({
        pluginDeploymentId: instance?.deployment?.id,
        name: customName,
        description: `Auto-forked from official preset: ${presetName}`,
        config: configToFork,
        isAiGenerated: false,
      });

      console.log('[ConfigEditor] ✅ Preset created:', {
        resultId: result.id,
        resultName: result.name,
        resultPluginDeploymentId: result.pluginDeploymentId,
      });

      const newPresetId = `custom:${result.id}`;
      setSelectedPresetId(newPresetId);

      // Invalidate preset queries to refresh the list
      console.log(
        '[ConfigEditor] 🔄 Invalidating preset list for pluginDeploymentId:',
        instance?.deployment?.id,
      );
      await utils.pluginPresets.list.invalidate({ pluginDeploymentId: instance?.deployment?.id });

      // Update config metadata to point to forked preset
      const updatedConfig = {
        ...configToFork,
        meta: {
          ...(configToFork as any).meta,
          presetId: newPresetId,
          presetName: customName,
          presetType: 'custom',
        },
      };

      // Update the local state
      setConfigData(updatedConfig);
      setEditableConfigName(customName);
      if (editMode === 'json') {
        setJsonConfig(JSON.stringify(updatedConfig, null, 2));
      }

      // Now allow editing
      hasUserEditedRef.current = true;

      toast({
        title: t('configEditor.editor.customPresetCreated'),
        description: t('configEditor.editor.customPresetCreatedDesc', { name: customName }),
      });

      return true;
    } catch (error: any) {
      console.error('[ConfigEditor] ❌ Failed to auto-fork preset:', error);
      toast({
        title: t('configEditor.editor.forkFailed'),
        description: error.message || t('configEditor.editor.failedCreateCustom'),
        variant: 'destructive',
      });
      return false;
    }
  };

  // Handle language change - updates config and auto-forks official presets
  const handleLanguagesChange = async (newLanguages: string[]) => {
    // Check if editing an official preset - auto-fork it
    if (!hasUserEditedRef.current && selectedPresetId?.startsWith('deployment:')) {
      // IMMEDIATELY mark as edited to prevent duplicate forks
      hasUserEditedRef.current = true;
      // Auto-fork the preset with the language change applied
      await autoForkAndApplyChanges({ languages: newLanguages });
      return;
    }

    // Apply the language change to config
    hasUserEditedRef.current = true;
    setConfigData((prev) => ({
      ...prev,
      languages: newLanguages,
    }));
  };

  // AI config editor mutation for translation
  const translateConfigMutation = trpc.aiConfigEditor.editConfig.useMutation();
  // Manual language addition mutation (copies source translations)
  const addLanguageManualMutation = trpc.aiConfigEditor.addLanguageManual.useMutation();

  // Handle adding a new language with optional AI translation
  const handleAddLanguageWithTranslation = useCallback(
    async (request: AddLanguageRequest) => {
      const { targetLanguage, sourceLanguage, translationMode } = request;

      console.log('[ConfigEditor] 🌐 Adding language:', {
        targetLanguage,
        sourceLanguage,
        translationMode,
      });

      // Get language names for the prompt
      const targetLangInfo = LANGUAGES_LIST.find((l) => l.code === targetLanguage);
      const sourceLangInfo = LANGUAGES_LIST.find((l) => l.code === sourceLanguage);

      if (translationMode === 'manual') {
        // Manual mode: copy translations from source language to target language
        // Prevent duplicate calls
        if (addLanguageManualMutation.isPending) {
          console.log('[ConfigEditor] ⚠️ Manual add already in progress, skipping duplicate call');
          return;
        }

        try {
          const result = await addLanguageManualMutation.mutateAsync({
            config: configData,
            sourceLanguage,
            targetLanguage,
          });

          if (result.success && result.config) {
            // Check if we need to auto-fork official preset
            if (!hasUserEditedRef.current && selectedPresetId?.startsWith('deployment:')) {
              await autoForkAndApplyChanges(result.config);
            } else {
              hasUserEditedRef.current = true;
              setConfigData(result.config);
            }

            toast({
              title: t('configEditor.language.languageAdded'),
              description: t('configEditor.language.languageAddedDesc', { name: targetLangInfo?.name || targetLanguage, count: result.translationsCopied, source: sourceLangInfo?.name || sourceLanguage }),
            });
          }
        } catch (error: any) {
          console.error('[ConfigEditor] Add language error:', error);
          toast({
            title: t('configEditor.language.addFailed'),
            description: error.message || t('configEditor.language.addFailedDesc'),
            variant: 'destructive',
          });
        }
        return;
      }

      // AI translation mode
      // Prevent duplicate calls (React StrictMode, double-clicks, etc.)
      if (translateConfigMutation.isPending) {
        console.log('[ConfigEditor] ⚠️ Translation already in progress, skipping duplicate call');
        return;
      }

      try {
        toast({
          title: t('configEditor.language.translating'),
          description: `${sourceLangInfo?.name || sourceLanguage} → ${targetLangInfo?.name || targetLanguage}`,
        });

        const result = await translateConfigMutation.mutateAsync({
          currentConfig: configData,
          prompt: `Add ${targetLangInfo?.name || targetLanguage} (${targetLanguage}) language to the configuration by translating all text content from ${sourceLangInfo?.name || sourceLanguage} (${sourceLanguage}). Keep the same structure and only translate user-facing text strings. Do not translate technical values, URLs, or code.`,
          schema: configSchema,
          deploymentId: instance?.deployment?.id,
          language: sourceLanguage, // Working language for the AI
          options: {
            language: sourceLanguage,
          },
        });

        if (result.success && result.config) {
          // Ensure the new language is in the languages array
          const resultLanguages = Array.isArray(result.config.languages) ? result.config.languages : [];
          const updatedConfig = {
            ...result.config,
            languages: [...new Set([...resultLanguages, targetLanguage])],
          };

          // Check if we need to auto-fork official preset
          if (!hasUserEditedRef.current && selectedPresetId?.startsWith('deployment:')) {
            await autoForkAndApplyChanges(updatedConfig);
          } else {
            hasUserEditedRef.current = true;
            setConfigData(updatedConfig);
          }

          toast({
            title: t('configEditor.language.translationComplete'),
            description: t('configEditor.language.translationCompleteDesc', { name: targetLangInfo?.name || targetLanguage }),
          });
        } else {
          throw new Error('Translation failed');
        }
      } catch (error: any) {
        console.error('[ConfigEditor] Translation error:', error);
        toast({
          title: t('configEditor.language.translationFailed'),
          description: error.message || t('configEditor.language.translationFailedDesc'),
          variant: 'destructive',
        });
      }
    },
    [
      configData,
      configSchema,
      instance?.deployment?.id,
      selectedPresetId,
      autoForkAndApplyChanges,
      translateConfigMutation,
      addLanguageManualMutation,
      toast,
    ],
  );

  // Remove language mutation
  const removeLanguageMutation = trpc.aiConfigEditor.removeLanguage.useMutation();

  // Handle removing a language (backend filters all translation keys)
  const handleRemoveLanguage = useCallback(
    async (langToRemove: string) => {
      console.log('[ConfigEditor] 🗑️ Removing language:', langToRemove);

      const langInfo = LANGUAGES_LIST.find((l) => l.code === langToRemove);

      try {
        const result = await removeLanguageMutation.mutateAsync({
          config: configData,
          schema: configSchema,
          languageToRemove: langToRemove,
        });

        if (result.success && result.config) {
          // Check if we need to auto-fork official preset
          if (!hasUserEditedRef.current && selectedPresetId?.startsWith('deployment:')) {
            await autoForkAndApplyChanges(result.config);
          } else {
            hasUserEditedRef.current = true;
            setConfigData(result.config);
          }

          toast({
            title: t('configEditor.language.languageRemoved'),
            description: t('configEditor.language.languageRemovedDesc', { name: langInfo?.name || langToRemove }),
          });
        }
      } catch (error: any) {
        console.error('[ConfigEditor] Remove language error:', error);
        toast({
          title: t('configEditor.language.removeFailed'),
          description: error.message || t('configEditor.language.removeFailedDesc'),
          variant: 'destructive',
        });
      }
    },
    [configData, configSchema, selectedPresetId, autoForkAndApplyChanges, removeLanguageMutation, toast],
  );

  // Show loading state
  if (isLoading || !instance) {
    return (
      <div className="bg-background fixed inset-0 z-[9999] flex items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  const editorContent = (
    <LanguageProvider
      availableLanguages={configLanguages}
      onLanguagesChange={handleLanguagesChange}
      onAddLanguageWithTranslation={handleAddLanguageWithTranslation}
      onRemoveLanguage={handleRemoveLanguage}
    >
      <div className="bg-background fixed inset-0 z-[9999] flex flex-col">
        {/* Enhanced Header - Compact */}
        <div className="border-border bg-card relative flex items-center justify-between border-b px-4 py-2.5 shadow-sm">
          {/* Left Side: Title */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <h2 className="text-sm font-semibold leading-none tracking-tight">{t('configEditor.editor.title')}</h2>
              <p className="text-muted-foreground mt-1 text-[10px] font-medium">{t('configEditor.editor.subtitle')}</p>
            </div>
          </div>

          {/* Center: Config Name (Click to Edit) */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="group relative flex items-center justify-center">
              <Input
                value={editableConfigName}
                onChange={(e) => handleConfigNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className="hover:border-border hover:bg-muted/50 focus:border-input focus:bg-background focus:ring-ring peer h-9 w-64 border-transparent bg-transparent text-center text-lg font-bold shadow-none transition-all focus:ring-1"
                placeholder={t('configEditor.editor.untitledConfig')}
              />
              <span className="text-muted-foreground absolute right-2 opacity-0 transition-opacity group-hover:opacity-50 peer-focus:opacity-0">
                <Code className="h-3 w-3" />
              </span>
            </div>
          </div>

          {/* Right Side: Actions */}
          <div className="flex items-center gap-3">
            {/* Auto-save indicator */}
            <div className="flex items-center gap-2">
              {isSaving ? (
                <div className="bg-muted/50 flex items-center gap-1.5 rounded-full px-2 py-1">
                  <Loader2 className="text-primary h-3 w-3 animate-spin" />
                  <span className="text-muted-foreground text-[10px] font-medium">{t('configEditor.editor.saving')}</span>
                </div>
              ) : lastSaved ? (
                <div className="flex items-center gap-1.5 rounded-full bg-green-500/10 px-2 py-1">
                  <Check className="h-3 w-3 text-green-600" />
                  <span className="text-[10px] font-medium text-green-700">
                    Saved {new Date(lastSaved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseClick}
                disabled={isSaving}
                className="h-8 px-3 text-xs font-medium shadow-sm"
                title={isSaving ? 'Please wait while saving...' : undefined}
              >
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                {t('configEditor.editor.exit')}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content - Split View */}
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          {/* Left Panel - Config Editor */}
          <ResizablePanel defaultSize={33} minSize={25} maxSize={60}>
            <div className="border-border bg-muted/20 flex h-full flex-col border-r">
              {/* Enhanced Toolbar - Compact */}
              <div className="border-border bg-card flex items-center gap-3 border-b px-4 py-2.5 shadow-sm">
                {/* Presets Section */}
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                    {t('configEditor.editor.presetLabel')}
                  </span>
                  <PresetManager
                    deploymentId={instance?.deploymentId}
                    pluginDeploymentId={instance?.deployment?.id}
                    deploymentPresets={configPresets}
                    currentConfig={configData}
                    selectedPresetId={selectedPresetId}
                    onPresetSelect={async (config, presetInfo) => {
                      // Store preset information in config metadata
                      const updatedConfig = {
                        ...config,
                        meta: {
                          ...(config as any).meta,
                          presetId: presetInfo.id,
                          presetName: presetInfo.name,
                          presetType: presetInfo.type,
                        },
                      };

                      // Update local state
                      setConfigData(updatedConfig);
                      setSelectedPresetId(presetInfo.id);

                      // For official presets: save instance config immediately (without creating new preset)
                      // For custom presets: mark as edited for auto-save
                      if (presetInfo.type === 'deployment') {
                        // Save the official preset config to instance immediately
                        try {
                          await updateConfigMutation.mutateAsync({
                            instanceId,
                            config: updatedConfig,
                          });
                          lastSavedConfigRef.current = updatedConfig;
                          hasUserEditedRef.current = false; // Reset so first edit triggers auto-fork
                          toast({
                            title: t('configEditor.editor.presetApplied'),
                            description: t('configEditor.editor.presetAppliedOfficial', { name: presetInfo.name }),
                          });
                        } catch (error) {
                          console.error('[ConfigEditor] Failed to save preset:', error);
                          toast({
                            title: t('configEditor.editor.failedApplyPreset'),
                            description: t('configEditor.editor.failedApplyPresetDesc'),
                            variant: 'destructive',
                          });
                        }
                      } else {
                        // Custom preset: mark as edited for auto-save
                        hasUserEditedRef.current = true;
                        toast({
                          title: t('configEditor.editor.presetApplied'),
                          description: t('configEditor.editor.presetAppliedCustom', { name: presetInfo.name }),
                        });
                      }
                    }}
                  />
                </div>

                {/* Separator */}
                <div className="bg-border h-5 w-px" />

                {/* AI Assistant Toggle - Coming Soon */}
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  className="h-8 gap-1.5 px-3 text-xs font-medium shadow-sm transition-all opacity-60 cursor-not-allowed"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>{t('configEditor.editor.aiAssistant')}</span>
                  <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    {t('configEditor.editor.comingSoon')}
                  </span>
                </Button>
              </div>
              <div className="flex-1 overflow-auto">
                {/* UI Mode - Generated Form with Tabs and Accordions */}
                {editMode === 'ui' && (
                  <GroupedFormEditor
                    schema={configSchema}
                    uiSchema={configUiSchema}
                    formData={configData}
                    onChange={handleFormChange}
                    widgets={widgets}
                    fields={fields}
                    storeId={storeId}
                    currentPagePath={currentPagePath}
                    availablePagePaths={previewPages?.map((p) => p.path)}
                    onFieldFocus={(fieldPath) => {
                      // Convert field path (e.g., "branding.logoUrl") to element ID format (e.g., "config.branding.logoUrl")
                      const elementId = fieldPath.startsWith('config.') ? fieldPath : `config.${fieldPath}`;
                      previewRef.current?.applyStylesToElement(elementId, {
                        outline: '3px solid #ef4444', // Very visible, bright red ring
                        boxShadow: '0 0 0 4px #fecaca', // Softer outer red glow for extra emphasis
                      });
                    }}
                  />
                )}

                {/* JSON Mode - Raw JSON Editor */}
                {editMode === 'json' && (
                  <textarea
                    value={jsonConfig}
                    onChange={(e) => handleJsonChange(e.target.value)}
                    className="bg-muted focus:ring-primary h-full w-full resize-none rounded-md border p-4 font-mono text-xs focus:outline-none focus:ring-2"
                    spellCheck={false}
                  />
                )}
              </div>

              {/* Mode Switcher - Bottom */}
              <div className="border-border bg-card flex items-center justify-center border-t px-4 py-2">
                <Tabs
                  value={editMode}
                  onValueChange={(v) => setEditMode(v as 'ui' | 'json')}
                  className="w-auto"
                >
                  <TabsList className="bg-muted h-6 p-0.5">
                    <TabsTrigger
                      value="ui"
                      className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground h-5 px-2 text-[9px] font-medium data-[state=active]:shadow-sm"
                    >
                      {t('configEditor.editor.visual')}
                    </TabsTrigger>
                    <TabsTrigger
                      value="json"
                      className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground h-5 gap-1 px-2 text-[9px] font-medium data-[state=active]:shadow-sm"
                    >
                      <Code className="h-2.5 w-2.5" />
                      {t('configEditor.editor.code')}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Right Panel - Live Preview with Page Tabs */}
          <ResizablePanel defaultSize={67} minSize={40}>
            {storeId ? (
              <PreviewWithPageTabs
                ref={previewRef}
                pages={previewPages || []}
                deploymentPages={instance?.deployment?.pages}
                storeId={storeId}
                instanceId={instanceId}
                iframeKey={iframeKey}
                onPageChange={setCurrentPagePath}
                initialPagePath={initialPagePath}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <Loader2 className="text-primary mx-auto h-8 w-8 animate-spin" />
                  <p className="text-muted-foreground mt-4 text-sm">{t('configEditor.editor.loadingPreview')}</p>
                </div>
              </div>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>

        {/* Fork Preset Dialog */}
        <Dialog open={showForkDialog} onOpenChange={setShowForkDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('configEditor.editor.createCustomPreset')}</DialogTitle>
              <DialogDescription>
                {t('configEditor.editor.editingOfficialPreset')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center space-x-2">
              <Input
                value={forkPresetName}
                onChange={(e) => setForkPresetName(e.target.value)}
                placeholder={t('configEditor.editor.enterPresetName')}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleForkPreset(forkPresetName);
                  } else if (e.key === 'Escape') {
                    setShowForkDialog(false);
                  }
                }}
                className="flex-1"
              />
            </div>
            <DialogFooter className="sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForkDialog(false);
                  setForkPresetName('');
                }}
              >
                {t('configEditor.preset.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => handleForkPreset(forkPresetName)}
                disabled={!forkPresetName.trim() || createPresetMutation.isPending}
              >
                {createPresetMutation.isPending ? t('configEditor.editor.creating') : t('configEditor.editor.createPreset')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LanguageProvider>
  );

  // Use portal to render at body level (above all other content)
  return typeof document !== 'undefined' ? ReactDOM.createPortal(editorContent, document.body) : null;
}
